import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { auditPaperQuery, downloadAuditPdf } from '../../api/auditReport'
import { ApiError } from '../../api/client'
import { Forbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'

/**
 * `settings/Audit::paper` - fotografia ca document de tiparit, la
 * /app/settings/audit/paper/$id. Macheta: `settings/audit/paper.php`.
 *
 * In portal pagina se incarca FARA sablonul portalului - fara meniuri, fara
 * butoane, fara sertarul agentului - fiindca un audit se preda pe hartie sau
 * ca PDF. Aici documentul vine gata randat de server, din aceeasi macheta
 * din care iese si PDF-ul, si sta intr-un cadru: pagina adauga doar butonul
 * de descarcare (in portal era un rand in capul documentului) si drumul
 * inapoi la fotografie.
 */
export function AuditPaperPage({ id }: { id: number }) {
  const page = useQuery(auditPaperQuery(id))
  const [said, setSaid] = useState('')
  const download = useMutation({
    mutationFn: () => downloadAuditPdf(id),
    onMutate: () => setSaid(''),
    onError: (e: unknown) => setSaid(e instanceof ApiError ? e.message : 'The PDF could not be produced.'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div id="content" className="od-audit">
          <p>No such run. <a href="/app/settings/audit">Back to IT Audit</a>.</p>
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  const { html, title, run } = page.data

  return (
    <Content>
      <div id="content" className="od-audit od-audit-paper">
        <p className="od-audit-paper-bar">
          <button type="button" className="btn btn-primary" onClick={() => download.mutate()} disabled={download.isPending}>
            <i className="far fa-file-pdf" aria-hidden="true" /> {download.isPending ? 'Preparing…' : 'Download as PDF'}
          </button>{' '}
          <span className="text-muted">
            one file, the same for everybody. Printing this page from the browser gives a different document to every reader.
          </span>{' '}
          <Link className="btn btn-default" to="/settings/audit/snapshot/$id" params={{ id: String(run.ID) }}>
            Back to the snapshot
          </Link>
          {said !== '' && <span className="text-danger"> {said}</span>}
        </p>
        <iframe
          className="od-audit-paper-frame"
          title={title}
          srcDoc={html}
          sandbox=""
          style={{ width: '100%', minHeight: 'calc(100vh - 140px)', border: '1px solid #ddd', background: '#fff' }}
        />
      </div>
    </Content>
  )
}
